import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import { fmtMoney, fmtNumber, fmtPercent, fmtMultiple } from "@/lib/format";
import { ArrowUpRight, ArrowDownRight, Minus, TrendingUp, TrendingDown, Info, AlertTriangle, Sliders, RotateCcw } from "lucide-react";
import {
  LineChart, Line, Area, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ComposedChart,
} from "recharts";

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtByKind(v, kind) {
  if (v == null) return "—";
  if (kind === "money") return fmtMoney(v);
  if (kind === "pct") return fmtPercent(v);
  if (kind === "multiple") return `${(+v).toFixed(2)}x`;
  return fmtNumber(v, 0);
}

/**
 * MomChip / VsPlanChip — same visual language as Dashboard/Recovery. Delta
 * chip = tinted background, "MoM" / "vs plan" label, arrow icon.
 */
function DeltaPill({ pct, invert = false, label, testid }) {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return null;
  const isUp = pct > 0.5, isDown = pct < -0.5;
  const isGood = invert ? isDown : isUp;
  const isBad = invert ? isUp : isDown;
  const color = isGood ? "text-evergreen bg-evergreen/10"
    : isBad ? "text-red-700 bg-red-700/10"
    : "text-midnight bg-midnight/10 opacity-70";
  const Icon = pct > 0 ? ArrowUpRight : pct < 0 ? ArrowDownRight : Minus;
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest ${color}`}
         data-testid={testid}>
      <span className="opacity-70">{label}</span>
      <Icon className="w-3 h-3" />
      {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
    </div>
  );
}

/**
 * Plan hero chart — the ONE plan-vs-actual view.
 * • Solid green area/line = revenue actual through last completed month
 * • Dashed navy line = original BHAG plan
 * • Solid navy line = adjusted plan (past = actuals, future = plan)
 * • Optional dotted overlay = what-if scenario override
 */
function PlanHeroChart({ months, whatIf }) {
  if (!months || !months.length) return null;
  // Build the display series. Actual line stops at last COMPLETED month
  // (current-month data would be partial and misleading). Adjusted plan =
  // past actuals + remaining BHAG (row 3). Original plan = row 2.
  const rows = months.map((m) => ({
    month: MONTH_ABBR[m.month_num - 1],
    plan_original: m.revenue_target_original || null,
    adjusted: m.revenue_adjusted_target,
    actual: m.in_past ? m.revenue_actual : null,
  }));
  // What-if overlay lives at the whole-month level (Aug→Dec by design).
  const rowsWithWhatIf = whatIf
    ? rows.map((r, i) => ({ ...r, what_if: whatIf[i] }))
    : rows;
  return (
    <div className="bg-warm-tint border-t-4 border-navy p-6" data-testid="plan-hero-chart">
      <div className="flex items-baseline justify-between mb-1 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-navy">Plan vs actual · year view</h2>
        <span className="text-[10px] uppercase tracking-widest opacity-60">
          Actual line excludes the current partial month
        </span>
      </div>
      <p className="text-xs text-midnight opacity-70 mb-4">
        <strong>Original plan</strong> = BHAG row 2, the goal set at the start of the year. <strong>Adjusted plan</strong> replaces past months with actuals so year-end reflects reality-to-date. <strong>Actual</strong> stops at the last completed month.
      </p>
      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={rowsWithWhatIf} margin={{ top: 12, right: 24, bottom: 4, left: 8 }}>
          <CartesianGrid stroke="#141B4D" strokeOpacity={0.08} vertical={false} />
          <XAxis dataKey="month" tick={{ fill: "#101820", fontSize: 11 }} />
          <YAxis tick={{ fill: "#101820", fontSize: 11 }}
                 tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
          <Tooltip contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "8px 12px" }}
                   itemStyle={{ color: "#F2E9DB" }}
                   labelStyle={{ color: "#FFD100", fontWeight: 700, marginBottom: 4 }}
                   formatter={(v) => v == null ? "—" : fmtMoney(v)} />
          <Legend wrapperStyle={{ fontSize: 11, color: "#101820" }} />
          <Line type="monotone" dataKey="plan_original" name="Original plan" stroke="#141B4D"
                strokeDasharray="5 5" strokeWidth={1.5} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="adjusted" name="Adjusted plan" stroke="#1D57A5"
                strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} connectNulls />
          <Line type="monotone" dataKey="actual" name="Actual" stroke="#006747"
                strokeWidth={2.5} dot={{ r: 4, fill: "#006747" }} isAnimationActive={false}
                activeDot={{ r: 5, fill: "#006747", stroke: "#FFD100", strokeWidth: 2 }} />
          {whatIf && (
            <Line type="monotone" dataKey="what_if" name="What-if"
                  stroke="#F58518" strokeDasharray="3 3" strokeWidth={2}
                  dot={{ r: 3, fill: "#F58518" }} isAnimationActive={false} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * AttainmentChart — kept largely as-is from the old Targets page (it was the
 * one section already following the "everything is a line" rule). Rendered
 * with the new visual language.
 */
function AttainmentChart({ months }) {
  if (!months || !months.length) return null;
  const rows = months.filter((m) => m.in_past)
    .map((m) => ({
      month: MONTH_ABBR[m.month_num - 1],
      revenue: m.revenue_attainment_pct,
      sales: m.sales_attainment_pct,
      calls: m.calls_attainment_pct,
    }));
  return (
    <div className="bg-warm-tint border-t-4 border-cobalt p-6" data-testid="plan-attainment-chart">
      <div className="flex items-baseline justify-between mb-1 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-navy">Attainment % over the year</h2>
        <span className="text-[10px] uppercase tracking-widest opacity-60">actual ÷ ORIGINAL target · 100% = on plan</span>
      </div>
      <p className="text-xs text-midnight opacity-70 mb-4">
        Measured against BHAG&apos;s original targets (row 2) — not the adjusted numbers — so a rising line here means the business is genuinely outpacing plan. Completed months only.
      </p>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={rows}>
          <CartesianGrid stroke="#141B4D" strokeOpacity={0.08} vertical={false} />
          <XAxis dataKey="month" tick={{ fill: "#101820", fontSize: 11 }} />
          <YAxis tick={{ fill: "#101820", fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
          <Tooltip contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "8px 12px" }}
                   itemStyle={{ color: "#F2E9DB" }}
                   labelStyle={{ color: "#FFD100", fontWeight: 700, marginBottom: 4 }}
                   formatter={(v) => v == null ? "—" : `${v}%`} />
          <Legend wrapperStyle={{ fontSize: 11, color: "#101820" }} />
          <ReferenceLine y={100} stroke="#101820" strokeDasharray="4 4" strokeOpacity={0.6}
                         label={{ value: "target", position: "insideRight", fill: "#101820", fontSize: 10 }} />
          <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#006747" strokeWidth={2} />
          <Line type="monotone" dataKey="sales" name="Sales" stroke="#1D57A5" strokeWidth={2} />
          <Line type="monotone" dataKey="calls" name="Calls Booked" stroke="#F58518" strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * MetricMini — one small line chart in the per-metric grid. Shows target
 * (dashed) + actual (solid), MoM chip, vs-plan chip.
 */
function MetricMini({ series }) {
  if (!series || !series.months) return null;
  const rows = series.months.map((m) => ({
    month: MONTH_ABBR[m.month_num - 1],
    target: m.target, actual: m.actual,
    in_past: m.in_past, is_current: m.is_current,
  }));
  const latest = [...rows].reverse().find((r) => r.actual != null);
  const prev = latest ? [...rows].reverse().find((r) => r.actual != null && MONTH_ABBR.indexOf(r.month) < MONTH_ABBR.indexOf(latest.month)) : null;
  const mom = latest && prev && prev.actual ? ((latest.actual - prev.actual) / Math.abs(prev.actual)) * 100 : null;
  const vsPlan = latest && latest.target ? ((latest.actual - latest.target) / Math.abs(latest.target)) * 100 : null;
  return (
    <div className="bg-warm border-t-4 border-cobalt p-4" data-testid={`plan-metric-${series.metric}`}>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{series.label}</div>
        <div className="text-[10px] uppercase tracking-widest opacity-40">{latest?.month || "—"}</div>
      </div>
      <div className="text-xl font-bold text-navy mb-1">{fmtByKind(latest?.actual, series.kind)}</div>
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <DeltaPill pct={mom} invert={!series.upper_better} label="MoM" testid={`plan-metric-${series.metric}-mom`} />
        {vsPlan != null && <DeltaPill pct={vsPlan} invert={!series.upper_better} label="vs plan" testid={`plan-metric-${series.metric}-vs-plan`} />}
      </div>
      <ResponsiveContainer width="100%" height={70}>
        <LineChart data={rows} margin={{ top: 4, right: 6, bottom: 2, left: 2 }}>
          <XAxis dataKey="month" tick={{ fill: "#141B4D", fontSize: 9, opacity: 0.55 }}
                 axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={20} />
          <YAxis hide domain={["dataMin", "dataMax"]} />
          <Tooltip formatter={(v) => v == null ? "—" : fmtByKind(v, series.kind)}
            contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "6px 10px", fontSize: 11 }}
            itemStyle={{ color: "#F2E9DB" }} labelStyle={{ color: "#FFD100", fontWeight: 700 }} />
          <Line type="monotone" dataKey="target" stroke="#141B4D" strokeDasharray="4 4"
                strokeWidth={1.25} dot={false} connectNulls isAnimationActive={false} />
          <Line type="monotone" dataKey="actual" stroke={series.upper_better ? "#006747" : "#1D57A5"}
                strokeWidth={2} dot={false} connectNulls isAnimationActive={false}
                activeDot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * ReconcileTimeline — replaces the 3 static reconciliation tables.
 * Shows absolute % delta over the year with a red band above the threshold.
 */
function ReconcileTimeline({ data }) {
  if (!data) return null;
  const threshold = data.mismatch_threshold_pct || 2.0;
  return (
    <div className="bg-warm-tint border-t-4 border-cobalt p-6" data-testid="plan-reconcile-timeline">
      <div className="flex items-baseline justify-between mb-1 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-navy">Reconciliation over time</h2>
        <span className="text-[10px] uppercase tracking-widest opacity-60">
          delta between sources · red band = &gt; {threshold}% off
        </span>
      </div>
      <p className="text-xs text-midnight opacity-70 mb-4">
        When did integrations drift? Each line is the % delta between two sources for a metric. Bars inside the red zone mean the sources are more than {threshold}% apart.
      </p>

      {/* HubSpot vs Sheet (revenue + sales) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ReconcileSubchart title="HubSpot vs Sheet · Revenue" testid="reconcile-hs-revenue"
                           data={data.hubspot_vs_sheet} field="revenue_delta_pct" threshold={threshold} />
        <ReconcileSubchart title="HubSpot vs Sheet · Sales" testid="reconcile-hs-sales"
                           data={data.hubspot_vs_sheet} field="sales_delta_pct" threshold={threshold} />
        <ReconcileSubchart title="Railtown vs BHAG · Google spend" testid="reconcile-rt-google"
                           data={data.railtown_vs_bhag} field="google_delta_pct" threshold={threshold} />
        <ReconcileSubchart title="Railtown vs BHAG · Meta spend" testid="reconcile-rt-meta"
                           data={data.railtown_vs_bhag} field="meta_delta_pct" threshold={threshold} />
        {data.slack_vs_sheet.length === 0 && (
          <div className="col-span-1 md:col-span-2 p-4 bg-warm border-l-4 border-cobalt/60 text-xs text-midnight opacity-70">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 shrink-0 text-cobalt" />
              <div>
                <strong className="text-navy">Slack vs Sheet timeline coming online.</strong>{" "}
                We only started logging paired Slack + Sheet snapshots recently — the historical
                line will populate as more daily captures accumulate. Today&apos;s spot-check is on the Console.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReconcileSubchart({ title, data, field, threshold, testid }) {
  const rows = (data || []).map((r) => ({
    month: r.month,
    delta: r[field],
    over: r[field] != null && Math.abs(r[field]) > threshold ? r[field] : null,
  }));
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-cobalt font-bold mb-2">{title}</div>
      <ResponsiveContainer width="100%" height={140}>
        <ComposedChart data={rows} margin={{ top: 6, right: 6, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="#141B4D" strokeOpacity={0.08} vertical={false} />
          <XAxis dataKey="month" tick={{ fill: "#101820", fontSize: 10 }} />
          <YAxis tick={{ fill: "#101820", fontSize: 10 }} tickFormatter={(v) => `${v}%`}
                 domain={["auto", "auto"]} />
          <Tooltip contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "6px 10px", fontSize: 11 }}
                   itemStyle={{ color: "#F2E9DB" }}
                   labelStyle={{ color: "#FFD100", fontWeight: 700 }}
                   formatter={(v) => v == null ? "—" : `${v}%`} />
          <ReferenceLine y={threshold} stroke="#b91c1c" strokeDasharray="3 3" strokeOpacity={0.5} />
          <ReferenceLine y={-threshold} stroke="#b91c1c" strokeDasharray="3 3" strokeOpacity={0.5} />
          <ReferenceLine y={0} stroke="#141B4D" strokeOpacity={0.5} />
          <Line type="monotone" dataKey="delta" stroke="#1D57A5" strokeWidth={2}
                dot={{ r: 3 }} isAnimationActive={false} connectNulls />
          <Line type="monotone" dataKey="over" stroke="#b91c1c" strokeWidth={0}
                dot={{ r: 5, fill: "#b91c1c", stroke: "#FFD100", strokeWidth: 2 }}
                isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * ScenarioModeler — multi-variable what-if for the projection month.
 * 5 sliders + Google/Meta split. Defaults come from BHAG trailing-3-mo
 * averages. Projects calls booked → held → closed-won → revenue and
 * compares against the adjusted plan target for the projection month.
 */
function ScenarioSlider({ label, unit, min, max, step, value, defaultValue, onChange, testid, format }) {
  const displayFmt = format || ((v) => v);
  const isDirty = Math.abs(value - defaultValue) > 0.0001;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-[10px] uppercase tracking-widest text-cobalt font-bold">{label}</label>
        <div className="text-sm font-bold text-navy tabular-nums">
          {displayFmt(value)}<span className="opacity-50 text-[10px] ml-0.5">{unit}</span>
        </div>
      </div>
      <input type="range" min={min} max={max} step={step}
             value={value}
             onChange={(e) => onChange(parseFloat(e.target.value))}
             className="w-full accent-[#1D57A5]"
             data-testid={`${testid}-input`} />
      <div className="flex justify-between text-[9px] uppercase tracking-widest opacity-40 mt-0.5">
        <span>{displayFmt(min)}</span>
        <span className={isDirty ? "text-cobalt font-bold" : "opacity-70"}>
          baseline {displayFmt(defaultValue)}
        </span>
        <span>{displayFmt(max)}</span>
      </div>
    </div>
  );
}

function ScenarioModeler({ baseline }) {
  const [paidSpend, setPaidSpend] = useState(0);
  const [googleShare, setGoogleShare] = useState(70);
  const [closeRate, setCloseRate] = useState(0);
  const [showUpRate, setShowUpRate] = useState(0);
  const [avgDeal, setAvgDeal] = useState(0);
  const [cpl, setCpl] = useState(0);
  const [initialized, setInitialized] = useState(false);

  // Initialize sliders from baseline once it loads.
  useEffect(() => {
    if (!baseline || initialized) return;
    const b = baseline.baselines;
    setPaidSpend(Math.round(b.paid_spend_monthly || 0));
    setGoogleShare(Math.round(b.google_share_pct || 70));
    setCloseRate(b.close_rate_pct || 0);
    setShowUpRate(b.show_up_rate_pct || 0);
    setAvgDeal(Math.round(b.avg_deal_value || 0));
    setCpl(Math.round(b.blended_cpl || 0));
    setInitialized(true);
  }, [baseline, initialized]);

  const resetAll = () => {
    if (!baseline) return;
    const b = baseline.baselines;
    setPaidSpend(Math.round(b.paid_spend_monthly || 0));
    setGoogleShare(Math.round(b.google_share_pct || 70));
    setCloseRate(b.close_rate_pct || 0);
    setShowUpRate(b.show_up_rate_pct || 0);
    setAvgDeal(Math.round(b.avg_deal_value || 0));
    setCpl(Math.round(b.blended_cpl || 0));
  };

  const projection = useMemo(() => {
    if (!baseline || cpl <= 0) return null;
    const calls_booked = paidSpend / cpl;
    const calls_held = calls_booked * (showUpRate / 100);
    const closed_won = calls_held * (closeRate / 100);
    const revenue = closed_won * avgDeal;
    return {
      calls_booked: Math.round(calls_booked),
      calls_held: Math.round(calls_held),
      closed_won: Math.round(closed_won * 10) / 10,
      revenue: Math.round(revenue),
    };
  }, [baseline, paidSpend, cpl, showUpRate, closeRate, avgDeal]);

  if (!baseline) {
    return (
      <div className="bg-warm-tint border-t-4 border-cobalt p-6">
        <div className="text-xs opacity-60">Loading scenario baseline…</div>
      </div>
    );
  }

  const b = baseline.baselines;
  const planTarget = baseline.plan_targets?.adjusted_revenue_target || 0;
  const revenueDelta = projection ? projection.revenue - planTarget : 0;
  const revenueDeltaPct = planTarget ? (revenueDelta / planTarget) * 100 : 0;
  const hitsPlan = revenueDelta >= 0;
  const isDirty = initialized && (
    Math.abs(paidSpend - (b.paid_spend_monthly || 0)) > 1 ||
    Math.abs(googleShare - (b.google_share_pct || 70)) > 0.5 ||
    Math.abs(closeRate - (b.close_rate_pct || 0)) > 0.1 ||
    Math.abs(showUpRate - (b.show_up_rate_pct || 0)) > 0.1 ||
    Math.abs(avgDeal - (b.avg_deal_value || 0)) > 1 ||
    Math.abs(cpl - (b.blended_cpl || 0)) > 1
  );

  const googleSpend = paidSpend * (googleShare / 100);
  const metaSpend = paidSpend - googleSpend;

  return (
    <div className="bg-warm-tint border-t-4 border-navy p-6" data-testid="scenario-modeler">
      <div className="flex items-baseline justify-between mb-1 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sliders className="w-5 h-5 text-navy" />
          <h2 className="text-xl font-bold text-navy">Scenario modeler · {baseline.projection_month.month_label}</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest opacity-60" data-testid="scenario-source">
            Baselines: {baseline.source}
          </span>
          {isDirty && (
            <button onClick={resetAll}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-cobalt underline"
                    data-testid="scenario-reset">
              <RotateCcw className="w-3 h-3" /> Reset to baseline
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-midnight opacity-70 mb-6">
        Move the five levers to model a scenario for the projection month. Baselines are the trailing-3-completed-month averages from BHAG (
        {baseline.trailing_3mo_months.map((m) => m.month_label.slice(0, 3)).join(" · ")}).
        The funnel is <strong>paid spend ÷ blended CPL</strong> → <strong>calls booked</strong> → <strong>× show-up rate</strong> → <strong>calls held</strong> → <strong>× close rate</strong> → <strong>× avg deal value</strong> → <strong>revenue</strong>.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Sliders — 3 columns */}
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <ScenarioSlider label="Paid spend (monthly)" unit="" testid="scenario-spend"
                            min={0} max={Math.max(50000, Math.round((b.paid_spend_monthly || 10000) * 2.5))}
                            step={500} value={paidSpend} defaultValue={Math.round(b.paid_spend_monthly || 0)}
                            onChange={setPaidSpend} format={(v) => `$${(v || 0).toLocaleString()}`} />
            <div className="mt-2 text-[10px] uppercase tracking-widest opacity-60">
              Google share <span className="text-navy font-bold">{googleShare}%</span> · Meta share <span className="text-navy font-bold">{100 - googleShare}%</span>
            </div>
            <input type="range" min={0} max={100} step={5}
                   value={googleShare}
                   onChange={(e) => setGoogleShare(parseInt(e.target.value))}
                   className="w-full accent-[#1D57A5] mt-1"
                   data-testid="scenario-google-share-input" />
            <div className="flex justify-between text-[9px] uppercase tracking-widest opacity-40 mt-0.5">
              <span>0/100</span>
              <span className="opacity-70">baseline {Math.round(b.google_share_pct || 70)}/{100 - Math.round(b.google_share_pct || 70)}</span>
              <span>100/0</span>
            </div>
            <div className="text-[10px] opacity-60 mt-1">
              → Google spend <span className="font-mono text-navy">${Math.round(googleSpend).toLocaleString()}</span> · Meta spend <span className="font-mono text-navy">${Math.round(metaSpend).toLocaleString()}</span>
            </div>
          </div>

          <ScenarioSlider label="Blended CPL" unit="" testid="scenario-cpl"
                          min={10} max={Math.max(400, Math.round((b.blended_cpl || 100) * 3))}
                          step={5} value={cpl} defaultValue={Math.round(b.blended_cpl || 0)}
                          onChange={setCpl} format={(v) => `$${(v || 0).toLocaleString()}`} />

          <ScenarioSlider label="Show-up rate" unit="%" testid="scenario-showup"
                          min={0} max={100} step={1}
                          value={showUpRate} defaultValue={Math.round((b.show_up_rate_pct || 0) * 10) / 10}
                          onChange={setShowUpRate} format={(v) => (v || 0).toFixed(0)} />

          <ScenarioSlider label="Close rate" unit="%" testid="scenario-close"
                          min={0} max={100} step={1}
                          value={closeRate} defaultValue={Math.round((b.close_rate_pct || 0) * 10) / 10}
                          onChange={setCloseRate} format={(v) => (v || 0).toFixed(0)} />

          <ScenarioSlider label="Avg deal value" unit="" testid="scenario-deal"
                          min={0} max={Math.max(20000, Math.round((b.avg_deal_value || 5000) * 2.5))}
                          step={100} value={avgDeal} defaultValue={Math.round(b.avg_deal_value || 0)}
                          onChange={setAvgDeal} format={(v) => `$${(v || 0).toLocaleString()}`} />
        </div>

        {/* Output — 2 columns */}
        <div className="lg:col-span-2 bg-navy text-warm p-5 space-y-4" data-testid="scenario-output">
          <div className="text-[10px] uppercase tracking-[0.25em] text-yellow font-bold">Projected · {baseline.projection_month.month_label}</div>

          <div className="space-y-2">
            <FunnelRow label="Calls booked" value={projection?.calls_booked} format={fmtNumber} testid="scenario-out-booked" />
            <FunnelRow label="Calls held" value={projection?.calls_held} format={fmtNumber} testid="scenario-out-held" />
            <FunnelRow label="Closed-won" value={projection?.closed_won} format={(v) => (v || 0).toFixed(1)} testid="scenario-out-closed" />
          </div>

          <div className="pt-3 border-t border-yellow/20">
            <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">Projected revenue</div>
            <div className="text-3xl font-bold text-yellow tabular-nums" data-testid="scenario-out-revenue">
              {projection ? fmtMoney(projection.revenue) : "—"}
            </div>
          </div>

          {projection && planTarget > 0 && (
            <div className="pt-3 border-t border-yellow/20">
              <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">vs adjusted plan target</div>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 font-bold text-sm ${hitsPlan ? "bg-evergreen text-warm" : "bg-red-700 text-warm"}`}
                   data-testid="scenario-out-delta">
                {hitsPlan ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {revenueDelta >= 0 ? "+" : ""}{fmtMoney(revenueDelta)}
                <span className="opacity-70 text-xs font-normal">
                  ({revenueDeltaPct >= 0 ? "+" : ""}{revenueDeltaPct.toFixed(1)}%)
                </span>
              </div>
              <div className="text-[10px] opacity-60 mt-2">
                Plan target: {fmtMoney(planTarget)}
              </div>
            </div>
          )}

          {(!projection || cpl <= 0) && (
            <div className="text-xs opacity-70">
              Set a positive CPL to compute the projection.
            </div>
          )}
        </div>
      </div>

      <p className="text-[10px] opacity-50 mt-4">
        Scenario is a scratchpad — nothing is saved. Close-rate and show-up-rate baselines depend on BHAG&apos;s <code>calls_had_actual</code> and <code>sales_actual</code> being present in the trailing months.
      </p>
    </div>
  );
}

function FunnelRow({ label, value, format, testid }) {
  return (
    <div className="flex items-baseline justify-between text-sm" data-testid={testid}>
      <span className="opacity-70">{label}</span>
      <span className="font-bold text-warm tabular-nums">{value != null ? format(value) : "—"}</span>
    </div>
  );
}

export default function PlanPage() {
  const [data, setData] = useState(null);       // /targets/actual-vs-target
  const [metrics, setMetrics] = useState(null); // /analytics/plan-metric-series
  const [platformMonthly, setPlatformMonthly] = useState(null); // reuse Recovery's endpoint for ROAS
  const [recon, setRecon] = useState(null);     // /analytics/reconciliation-timeline
  const [scenarioBaseline, setScenarioBaseline] = useState(null);

  useEffect(() => {
    const y = new Date().getFullYear();
    Promise.all([
      api.get(`/targets/actual-vs-target?year=${y}`),
      api.get(`/analytics/plan-metric-series?year=${y}`),
      api.get(`/analytics/platform-monthly?months=12`),
      api.get(`/analytics/reconciliation-timeline?days=365`),
      api.get(`/analytics/scenario-baseline`),
    ]).then(([t, m, p, r, sb]) => {
      setData(t.data); setMetrics(m.data); setPlatformMonthly(p.data); setRecon(r.data);
      setScenarioBaseline(sb.data);
    }).catch(() => {});
  }, []);

  if (!data) return <div className="p-10 text-navy">Loading plan…</div>;
  const { totals } = data;
  const deltaVsPlan = totals.revenue_delta_vs_plan || 0;
  const deltaPct = totals.plan_revenue_year ? (deltaVsPlan / totals.plan_revenue_year) * 100 : 0;
  const pacingBehind = deltaVsPlan < -1000;

  return (
    <div data-testid="plan-page">
      <TitleBlock
        eyebrow="Plan & Reality"
        title="The Plan"
        subtitle="One forecast. One page. Every metric as a line over time. The plan reflects reality-to-date so year-end projections stay honest."
        right={
          <div className="text-right">
            <div className="text-yellow text-[10px] uppercase tracking-[0.28em]">Year-end projected</div>
            <div className="text-warm font-bold text-2xl" data-testid="plan-hero-projected">
              {fmtMoney(totals.adjusted_revenue_year)}
            </div>
            <div className={`text-xs mt-0.5 ${pacingBehind ? "text-red-700" : "text-warm/70"}`}
                 data-testid="plan-hero-delta">
              {deltaVsPlan >= 0 ? "+" : ""}{fmtMoney(deltaVsPlan)} vs original plan · {deltaPct.toFixed(1)}%
            </div>
          </div>
        }
      />

      <div className="p-6 md:p-8 space-y-8">

        {/* §1 · Hero chart */}
        <PlanHeroChart months={data.months} whatIf={null} />

        {/* §2 · Attainment strip */}
        <AttainmentChart months={data.months} />

        {/* §3 · Per-metric small-multiples grid */}
        {metrics && (
          <div>
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-cobalt font-bold">Per metric · target vs actual</span>
              <span className="text-xs opacity-50">every KPI as a 12-month line, dashed = plan, solid = actual</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="plan-metric-grid">
              {["revenue","sales","conv_rate","calls_booked","calls_had",
                "google_spend","meta_spend","google_cpl","meta_cpl",
                "google_roas","meta_roas","effective_roas"].map((k) => (
                <MetricMini key={k} series={metrics.series[k]} />
              ))}
            </div>
          </div>
        )}

        {/* §4 · Reconciliation timeline */}
        <ReconcileTimeline data={recon} />

        {/* §5 · Scenario modeler */}
        <ScenarioModeler baseline={scenarioBaseline} />
      </div>
    </div>
  );
}
